import { useMemo } from 'react';

import { AnimatePresence } from 'motion/react';

import { Skeleton } from '@/shadcn/skeleton';

import type { BatchJobSummary } from '@/hooks/useWorkflowBatchJobs';
import { BatchJobCard } from './batchJobCard';
import type { SubmitterIdentity } from './batchSubmitter';
import { groupJobsByDay } from './groupJobsByDay';

const LOAD_MORE_THRESHOLD_PX = 240;
const SKELETON_ROWS = 5;

type Props = {
  jobs: readonly BatchJobSummary[];
  selectedJobId: string | null;
  onSelect: (jobId: string) => void;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  now?: Date;
  // Reflects the active filter — "nothing ever ran here" is wrong when the
  // reader has merely narrowed to Running.
  emptyMessage?: string;
  // email -> display identity, so a card can show a person instead of an address.
  submitters?: Record<string, SubmitterIdentity>;
  // group label -> group name, for the same reason.
  groupNames?: Record<string, string>;
  // action key -> the icon the workflow config declares for it.
  actionIcons?: Record<string, string>;
};

export function BatchJobList({
  jobs,
  selectedJobId,
  onSelect,
  loading,
  hasMore,
  onLoadMore,
  now,
  emptyMessage = 'No batch action has been run on this workflow yet.',
  submitters,
  groupNames,
  actionIcons
}: Props) {
  const groups = useMemo(
    () => groupJobsByDay(jobs, now ?? new Date()),
    [jobs, now]
  );

  if (loading && jobs.length === 0) {
    return (
      <div
        className="flex flex-col gap-2 p-2.5"
        data-testid="batch-job-list-loading"
      >
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <Skeleton className="h-14 w-full rounded-lg" key={i} />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div
        className="flex h-full items-center justify-center p-6 text-center text-foreground/50 text-sm"
        data-testid="batch-job-list-empty"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      className="h-full overflow-auto"
      data-testid="batch-job-list"
      onScroll={e => {
        if (!hasMore) {
          return;
        }
        const el = e.currentTarget;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining < LOAD_MORE_THRESHOLD_PX) {
          onLoadMore();
        }
      }}
    >
      {groups.map(group => (
        <section key={group.key}>
          <h3 className="sticky top-0 z-1 bg-card px-3 pt-2 pb-1 font-bold text-[10px] text-foreground/60 uppercase tracking-wider">
            {group.label}
          </h3>
          <div className="flex flex-col gap-1.5 px-2.5 pb-1.5">
            <AnimatePresence initial={false}>
              {group.jobs.map(job => (
                <BatchJobCard
                  actionIcon={actionIcons?.[job.actionKey]}
                  groupName={groupNames?.[job.groupLabel]}
                  job={job}
                  key={job.id}
                  onSelect={onSelect}
                  selected={job.id === selectedJobId}
                  submitter={job.runBy ? submitters?.[job.runBy] : undefined}
                />
              ))}
            </AnimatePresence>
          </div>
        </section>
      ))}
    </div>
  );
}
